import { useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  FormControl,
  InputGroup,
  Nav,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap'
import { Link } from 'react-router-dom'
import KLayerNavigation from '../components/KLayerNavigation'
import { apiRequest, formatDate, isUserLoggedIn, showKAlert } from '../helpers'
import { t } from '../locale'
import { AdditionalFormSchema } from '../types/stammdaten.type'

const pageSizeOptions = [10, 20, 50]

type StammdatenType = 'company' | 'person' | 'all'

export default function FormSchemasListPage() {
  const [loading, setLoading] = useState(true)
  const [schemas, setSchemas] = useState<AdditionalFormSchema[]>([])
  const [error, setError] = useState<string>('')
  const [query, setQuery] = useState('')
  const [sortKey, setSortKey] = useState<'name' | 'updatedAt' | 'createdAt' | 'fieldsCount'>(
    'updatedAt'
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(pageSizeOptions[0])
  const [activeTab, setActiveTab] = useState<StammdatenType>('all')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError('')

      try {
        apiRequest(`${process.env.REACT_APP_API_URL}/additional/all`, 'GET').then(r => {
          if (!cancelled) {
            if (r?.err) return

            if (Array.isArray(r?.docs) && r.docs.length > 0) {
              setSchemas(r.docs)
            } else {
              setSchemas([])
            }
          }
        })
      } catch (e: any) {
        if (!cancelled) setError(e?.message || t('loadError'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  // filter
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    let filteredSchemas = schemas

    // Filter by stammdatenType
    if (activeTab !== 'all') {
      filteredSchemas = schemas.filter(s => s.stammdatenType === activeTab)
    }

    if (!q) return filteredSchemas
    return schemas.filter(
      s =>
        s.name.toLowerCase().includes(q) ||
        (s.fields || []).some(
          f => f.name.toLowerCase().includes(q) || (f.label || '').toLowerCase().includes(q)
        )
    )
  }, [schemas, query, activeTab])

  const handleSave = async (id: string, name: string, include: boolean, stammdatenType: string) => {
    const payload = {
      include: include,
      name: name,
      stammdatenType: stammdatenType,
    }

    try {
      if (id) {
        await apiRequest(`${process.env.REACT_APP_API_URL}/additional/${id}`, 'PUT', payload)
      }
    } catch (e: any) {
      showKAlert('danger', t('updateNotSuccess'))
    }
  }
  // sort
  const sorted = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      let va: any
      let vb: any
      if (sortKey === 'fieldsCount') {
        va = a.fields?.length ?? 0
        vb = b.fields?.length ?? 0
      } else if (sortKey === 'name') {
        va = a.name?.toLowerCase() ?? ''
        vb = b.name?.toLowerCase() ?? ''
      } else {
        // createdAt / updatedAt
        va = a[sortKey] ? new Date(a[sortKey]!).getTime() : 0
        vb = b[sortKey] ? new Date(b[sortKey]!).getTime() : 0
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return arr
  }, [filtered, sortKey, sortDir])

  // pagination
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  const pageSafe = Math.min(page, totalPages)
  const paged = useMemo(() => {
    const start = (pageSafe - 1) * pageSize
    return sorted.slice(start, start + pageSize)
  }, [sorted, pageSafe, pageSize])

  const changeSort = (key: typeof sortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const deleteForm = async (id: string) => {
    await apiRequest(`${process.env.REACT_APP_API_URL}/additional/${id}`, 'DELETE')

    const forms = schemas.filter(c => c._id !== id)
    setSchemas(forms)
  }

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>
        <main className='col-md-9 col-lg-10 ps-2 ps-md-3 px-md-4'>
          <Card className='border-0 mb-4'>
            <Card.Body>
              <Col>
                <h4 className='fw-bold mb-0'>
                  {t('formSchemas')} <Badge bg='secondary'>{schemas.length}</Badge>
                </h4>
                <div className='text-muted'>{t('manageSearchReuseForms')}</div>
              </Col>
              <Col className='d-flex justify-content-md-end mb-2'>
                <div className='d-flex gap-2'>
                  <Link to='/client/stammdaten/formular/company'>
                    <Button variant='primary'>{t('createCompanyForm')}</Button>
                  </Link>
                  <Link to='/client/stammdaten/formular/person'>
                    <Button variant='success'>{t('createPersonForm')}</Button>
                  </Link>
                </div>
              </Col>

              <Nav variant='tabs' className='mb-3'>
                <Nav.Item>
                  <Nav.Link active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
                    {t('All')} <Badge bg='secondary'>{schemas.length}</Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === 'company'}
                    onClick={() => setActiveTab('company')}
                  >
                    {t('Company')}{' '}
                    <Badge bg='primary'>
                      {schemas.filter(s => s.stammdatenType === 'company').length}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link active={activeTab === 'person'} onClick={() => setActiveTab('person')}>
                    {t('Person')}{' '}
                    <Badge bg='success'>
                      {schemas.filter(s => s.stammdatenType === 'person').length}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Row className='g-2 align-items-center mb-3'>
                <Col>
                  <InputGroup>
                    <InputGroup.Text>🔎</InputGroup.Text>
                    <FormControl
                      placeholder={t('searchByNameOrField')}
                      value={query}
                      onChange={e => {
                        setQuery(e.target.value)
                        setPage(1)
                      }}
                    />
                  </InputGroup>
                </Col>
                <Col>
                  <Form.Select
                    style={{ textTransform: 'none' }}
                    value={`${sortKey}:${sortDir}`}
                    onChange={e => {
                      const [k, d] = e.target.value.split(':') as [typeof sortKey, typeof sortDir]
                      setSortKey(k)
                      setSortDir(d)
                    }}
                  >
                    <option style={{ textTransform: 'none' }} value='updatedAt:desc'>
                      {t('sortByLastUpdateNewest')}
                    </option>
                    <option style={{ textTransform: 'none' }} value='updatedAt:asc'>
                      {t('sortByLastUpdateOldest')}
                    </option>
                    <option style={{ textTransform: 'none' }} value='name:asc'>
                      {t('sortByNameAZ')}
                    </option>
                    <option style={{ textTransform: 'none' }} value='name:desc'>
                      {t('sortByNameZA')}
                    </option>
                    <option style={{ textTransform: 'none' }} value='fieldsCount:desc'>
                      {t('sortByFieldsCountDesc')}
                    </option>
                    <option style={{ textTransform: 'none' }} value='fieldsCount:asc'>
                      {t('sortByFieldsCountAsc')}
                    </option>
                    <option style={{ textTransform: 'none' }} value='createdAt:desc'>
                      {t('sortByCreatedNewest')}
                    </option>
                    <option style={{ textTransform: 'none' }} value='createdAt:asc'>
                      {t('sortByCreatedOldest')}
                    </option>
                  </Form.Select>
                </Col>
                <Col>
                  <InputGroup>
                    <InputGroup.Text>{t('pageSize')}</InputGroup.Text>
                    <Form.Select
                      value={pageSize}
                      onChange={e => {
                        setPageSize(Number(e.target.value))
                        setPage(1)
                      }}
                    >
                      {pageSizeOptions.map(n => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                </Col>
              </Row>

              {loading ? (
                <div className='d-flex align-items-center gap-2 py-3'>
                  <Spinner animation='border' size='sm' />
                  <span>{t('loading')}</span>
                </div>
              ) : error ? (
                <div className='text-danger'>{error}</div>
              ) : (
                <>
                  <Table hover responsive className='align-middle'>
                    <thead>
                      <tr>
                        <th style={{ width: '34%' }}>
                          <Button
                            variant='link'
                            className='p-0 text-decoration-none'
                            onClick={() => changeSort('name')}
                          >
                            {t('Name')} {sortKey === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                          </Button>
                        </th>
                        <th style={{ width: '12%' }}></th>
                        <th style={{ width: '12%' }}>
                          <Button
                            variant='link'
                            className='p-0 text-decoration-none'
                            onClick={() => changeSort('fieldsCount')}
                          >
                            {t('Fields')}{' '}
                            {sortKey === 'fieldsCount' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                          </Button>
                        </th>
                        <th style={{ width: '15%' }}>
                          <Button
                            variant='link'
                            className='p-0 text-decoration-none'
                            onClick={() => changeSort('updatedAt')}
                          >
                            {t('Updated')}{' '}
                            {sortKey === 'updatedAt' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                          </Button>
                        </th>
                        <th style={{ width: '15%' }}>{t('edit')}</th>
                        <th style={{ width: '15%' }}>{t('includeInStammdaten')}</th>
                        <th style={{ width: '15%' }}>{t('change')}</th>
                        <th style={{ width: '15%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map(s => (
                        <tr key={s._id}>
                          <td>
                            <div className='fw-semibold'>{s.name}</div>
                            <div className='text-muted small'>ID: {s._id}</div>
                          </td>
                          <td>
                            <Badge bg={s.stammdatenType === 'company' ? 'primary' : 'success'}>
                              {s.stammdatenType === 'company' ? t('Company') : t('Person')}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg='secondary'>{s.fields?.length ?? 0}</Badge>
                          </td>
                          <td className='text-nowrap'>{formatDate(s.updatedAt || s.createdAt)}</td>
                          <td>
                            <div className='d-flex gap-2 flex-wrap'>
                              <Link
                                to={`/client/stammdaten/formular/${s.stammdatenType}/${encodeURIComponent(s._id)}`}
                              >
                                <Button size='sm' variant='outline-primary'>
                                  {t('Open')}
                                </Button>
                              </Link>
                            </div>
                          </td>
                          <td>
                            <div className='fw-semibold'>{s.include ? t('yes') : t('no')}</div>
                          </td>
                          <td>
                            <Button
                              size='sm'
                              variant='outline-primary'
                              onClick={() =>
                                handleSave(
                                  s._id,
                                  s.name,
                                  s.include ? false : true,
                                  s.stammdatenType
                                )
                              }
                            >
                              {s.include ? t('Exclude') : t('includeInStammdaten')}
                            </Button>
                          </td>
                          <td>
                            <Button size='sm' variant='danger' onClick={() => deleteForm(s._id)}>
                              {t('Delete')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!paged.length && (
                        <tr>
                          <td colSpan={5} className='text-center text-muted py-4'>
                            {t('noFormsFound')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination controls */}
                  <div className='d-flex justify-content-between align-items-center'>
                    <div className='text-muted small'>
                      {t('showing')} {(pageSafe - 1) * pageSize + 1}–
                      {Math.min(pageSafe * pageSize, sorted.length)} {t('of')} {sorted.length}
                    </div>
                    <div className='d-flex gap-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        onClick={() => setPage(1)}
                        disabled={pageSafe === 1}
                      >
                        « {t('First')}
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={pageSafe === 1}
                      >
                        {t('Prev')}
                      </Button>
                      <span className='small align-self-center'>
                        {t('Page')} {pageSafe}/{totalPages}
                      </span>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={pageSafe === totalPages}
                      >
                        {t('Next')}
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        onClick={() => setPage(totalPages)}
                        disabled={pageSafe === totalPages}
                      >
                        {t('Last')} »
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </main>
      </Row>
    </Container>
  )
}
